"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { HeaderComponent } from "../../../../components/pages/robot/components/header/HeaderComponent";
import { IPageProps } from "@portal/types/robot/IPageProps";
import {
  StartPage,
  ExperiencePage,
  RelevencePage,
  RefreshPage,
  PlanningPage,
  RiskPage,
  BehaviourPage,
  SustainabilityPage,
  LiquidityPage,
  SavingsPage,
  SharesPage,
  DebtPage,
} from "../../../../components/pages/robot";
import { FC, ReactNode, useEffect, useSyncExternalStore } from "react";
import { IsBankNewNorwegian } from "@portal/helpers";
import { useRobot } from "@portal/app/context/robotProvider";
import { ISavingsRobotPageClientComponentProps } from "./types";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";
import LayoutNotAuthorized from "@portal/components/organisms/layouts/Layout-NotAuthorized";
import useCenterIdOpenPages from "@portal/hooks/useCenterIdOpenPages";
import { SendMessageForResize } from "@portal/app/(iframe)/helpers/sendMessageForResize";
import { SustainabilityPreferencesPage } from "@portal/components/pages/robot/sustainabilityPreferences";

const subscribe = () => () => {};

const IFrameWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <div id="norne_frame" className="flex w-full bg-pageBoxBackgroundLight min-h-screen text-defaultText light">
      {children}
    </div>
  );
};

/**
 * Component to distribute the pages in the savings robot.
 * @returns a page.
 */
const SavingsRobotPageClientComponent: FC<ISavingsRobotPageClientComponentProps> = ({ session, id }) => {
  const searchParams = useSearchParams();
  const { isDefaultLanguage, setIsDefaultLanguage } = useRobot();
  const centerId = useCenterIdOpenPages();
  const router = useRouter();
  const isInIframe = useSyncExternalStore(subscribe, inIframe, () => false);

  const localId = IsValidId(id) ? id : "0";

  // Set default language
  useEffect(() => {
    if (centerId && IsBankNewNorwegian(centerId)) {
      setIsDefaultLanguage(false);
    }
  }, [centerId, setIsDefaultLanguage]);

  useEffect(() => {
    SendMessageForResize("sparerobot", "sparerobot");
  }, []);

  const status = searchParams?.get("status") as string;

  useEffect(() => {
    if (session?.status === "authenticated") {
      router.push(`/robot/authenticated/${localId}`);
    }
  }, [session, localId, router]);

  const renderRobot = () => (
    <div
      className="flex flex-col items-center m-auto mt-px w-full min-w-64 max-w-3xl border border-boxBorderColor dark:border-greyDark border-t-0 rounded-xl bg-robotBackground dark:bg-gray-800"
      id="sparerobot"
    >
      <HeaderComponent
        pageIndex={Number(localId)}
        centerId={centerId}
        isDefaultLanguage={isDefaultLanguage}
        isIFrame={isInIframe}
      />
      {GetPage({ centerId, pageIndex: Number(localId), isDefaultLanguage: isDefaultLanguage })}
    </div>
  );

  if (status === "loading") {
    return <PageSpinner />;
  }

  if (isInIframe) {
    return <IFrameWrapper>{renderRobot()}</IFrameWrapper>;
  }

  return <LayoutNotAuthorized centerId={centerId}>{renderRobot()}</LayoutNotAuthorized>;
};

function GetPage({ centerId, pageIndex, isDefaultLanguage }: IPageProps) {
  switch (pageIndex) {
    case 0:
      return <StartPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 1:
      return <ExperiencePage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 2:
      return <RelevencePage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 3:
      return <RefreshPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 4:
      return <PlanningPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 5:
      return <RiskPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 6:
      return <BehaviourPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 7:
      return <SustainabilityPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 8:
      return <SustainabilityPreferencesPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 9:
      return <LiquidityPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 10:
      return <SavingsPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 11:
      return <SharesPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 12:
      return <DebtPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    default:
      return <StartPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
  }
}

function IsValidId(id?: string) {
  return id !== undefined && Number(id) <= 12 && Number(id) > 0;
}

export const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export default SavingsRobotPageClientComponent;
